import * as React from "react";

import { DocumentNode } from "graphql";
import { getAppData } from "./query";
import { Query as QueryType } from "types";
import _ from "lodash";
import {
  QueryResult,
  RefetchQueriesProviderFn,
  MutationFn,
  MutationResult,
  Query,
  Mutation
} from "react-apollo";
import {
  OperationVariables,
  FetchPolicy,
  ErrorPolicy,
  ApolloClient,
  ApolloError,
  PureQueryOptions,
  MutationUpdaterFn
} from "apollo-client";

interface QueryProps<TData = any, TVariables = OperationVariables> {
  children: (result: QueryResult<TData, TVariables>) => React.ReactNode;
  fetchPolicy?: FetchPolicy;
  errorPolicy?: ErrorPolicy;
  notifyOnNetworkStatusChange?: boolean;
  pollInterval?: number;
  variables?: TVariables;
  ssr?: boolean;
  displayName?: string;
  skip?: boolean;
  client?: ApolloClient<object>;
  context?: Record<string, any>;
  onCompleted?: (data: TData | {}) => void;
  onError?: (error: ApolloError) => void;
}

export interface MutationProps<TData = any, TVariables = OperationVariables> {
  ignoreResults?: boolean;
  optimisticResponse?: TData;
  variables?: TVariables;
  refetchQueries?: (string | PureQueryOptions)[] | RefetchQueriesProviderFn;
  awaitRefetchQueries?: boolean;
  update?: MutationUpdaterFn<TData>;
  children: (
    mutateFn: MutationFn<TData, TVariables>,
    result: MutationResult<TData>
  ) => React.ReactNode;
  onCompleted?: (data: TData) => void;
  onError?: (error: ApolloError) => void;
  client?: ApolloClient<object>;
  context?: Record<string, any>;
}

interface BoundMutationOptions<TData> {
  mutation: DocumentNode;
  cacheKey?: keyof QueryType;
  isAdd?: boolean;
  isDelete?: boolean;
  update?: MutationUpdaterFn<TData>;
}

export function createBoundQuery<TData = any, TVariables = OperationVariables>(
  query: DocumentNode
) {
  return (props: QueryProps<TData, TVariables>) => {
    return (
      <Query<TData, TVariables> query={query} {...props}>
        {props.children}
      </Query>
    );
  };
}
export function createBoundMutation<
  TData = any,
  TVariables = OperationVariables
>({ mutation, cacheKey, isAdd, isDelete, update }: BoundMutationOptions<TData>) {
  const defaultUpdate: MutationUpdaterFn = (proxy, res) => {

    if (!cacheKey || cacheKey === "currentUser" || (!isAdd && !isDelete)) {
      return;
    }
    const query = proxy.readQuery<QueryType>({
      query: getAppData
    });
    const entity = Object.entries(res.data)[0][1] as any;
    const cached = query[cacheKey];

    if (isAdd){
      if (_.find(cached, { id: entity.id })){
        return;
      }
      proxy.writeQuery({
        query: getAppData,
        data: {
          ...query,
          [cacheKey]: [...cached, entity],
        }
      });
    }
    if (isDelete){
      proxy.writeQuery({
        query: getAppData,
        data: {
          ...query,
          [cacheKey]: _.filter(cached, c => (c as any).id !== entity),
        }
      });
    }

  };
  return class BoundMutation extends React.Component<
    MutationProps<TData, TVariables>
  > {
    public static Fn: MutationFn<TData, TVariables> = undefined;

    public render() {
      return (
        <Mutation<TData, TVariables>
          {...this.props}
          mutation={mutation}
          update={update || defaultUpdate}
        >
          {this.props.children}
        </Mutation>
      );
    }
  };
}



// WEBPACK FOOTER //
// ./src/api/util.tsx